import { PartnerSiteActivity } from "./PartnerSiteActivity";
import { ReasonCancelled, isReasonCancelled, useReasonDialog } from "@/shared/ui/ReasonDialog";   // REASON-1
import type { ReasonPayload } from "@/services/reasonCodes";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUp, ArrowRight } from "lucide-react";
import {
  partnersApi,
  ACTIVITY_LABEL,
  SETTLEMENT_MODE_LABEL,
  tierChip,
  type PartnerActivity,
  type PartnerStats,
  type Partner,
} from "@/services/b2b";
import { PageShell, Pill, LoadingState } from "@/shared/ui/PageHeader";
import { formatCents } from "@/services/finance";
import { Can, useCan } from "@/auth/permissions";
import { PartnerOpenApiTab } from "./PartnerOpenApiTab";
import { PartnerAccountsCard } from "./PartnerAccountsCard";
import { listCustomerPartners } from "@/services/customerPrices";   // PRICE-CUST-1
import { PartnerCreditOverview } from "./PartnerCreditOverview";
import { ReceivablesCard } from "./ReceivablesCard";
import { PartnerPaymentsCard } from "./PartnerPaymentsCard";   // REASON-2
import { RegisterPaymentModal } from "./RegisterPaymentModal";
import { PartnerStatementsCard } from "./PartnerStatementsCard";

/**
 * 客户详情 — 对齐 sign-off canvas「同业 B2B - 客户详情」(铁律十九)。
 * 头部(分层/月均/信用额度/健康度 + 暂停/降级/编辑) + 4 Tab：
 * 基础信息(公司信息+业务数据+健康度评分明细+API对接状态) / 专属定价(mirror→定价管理) /
 * 信用 & 结算(临时额度申请+审批) / 订单 & 对账(对账单+订单).
 */
const TABS = ["基础信息", "专属定价", "信用 & 结算", "订单 & 对账", "开放 API"] as const;
type Tab = (typeof TABS)[number];

const ACTIVITY_CHIP: Record<PartnerActivity, string> = {
  active: "bg-emerald-100 text-emerald-700",
  dormant: "bg-amber-100 text-amber-700",
  suspended: "bg-rose-100 text-rose-700",
};

export function ChannelPartnerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState<Tab>("基础信息");
  const partnerQ = useQuery({ queryKey: ["b2b", "partner", id], queryFn: () => partnersApi.get(id!), enabled: !!id });
  const statsQ = useQuery({ queryKey: ["b2b", "partner-stats", id], queryFn: () => partnersApi.partnerStats(id!), enabled: !!id });
  const qc = useQueryClient();
  const canUpdate = useCan("channel_partners.partners.update");
  // 用户 10-07 定：暂停 / 恢复、升降级、授信申请是独立权限点（老板 / 技术管理员 / 客户经理）
  const canStatus = useCan("channel_partners.partners.status_manage");
  const statusMut = useMutation({
    mutationFn: (status: "active" | "suspended") => partnersApi.setStatus(id!, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["b2b", "partner", id] });
      qc.invalidateQueries({ queryKey: ["b2b", "partner-stats", id] });
      qc.invalidateQueries({ queryKey: ["b2b", "board"] });
    },
    onError: (e) => alert((e as Error).message || "操作失败"),
  });
  const tiersQ = useQuery({ queryKey: ["b2b", "tiers"], queryFn: () => partnersApi.listTiers() });
  const [tierDialog, setTierDialog] = useState(false);
  const setTierMut = useMutation({
    mutationFn: (code: string | null) => partnersApi.setTier(id!, code),
    onSuccess: () => {
      setTierDialog(false);
      qc.invalidateQueries({ queryKey: ["b2b", "partner", id] });
      qc.invalidateQueries({ queryKey: ["b2b", "partner-stats", id] });
      qc.invalidateQueries({ queryKey: ["b2b", "board"] });
    },
    onError: (e) => alert((e as Error).message || "改分级失败"),
  });

  if (partnerQ.isLoading) return <LoadingState />;
  if (partnerQ.isError) return (
    <div className="flex flex-col items-center gap-2 py-16 text-[13px] text-slate-500">
      <span>客户加载失败</span>
      <button onClick={() => partnerQ.refetch()} className="rounded-md border border-slate-300 px-3 py-1 text-[12px] font-medium text-blue-600 hover:bg-blue-50">重试</button>
    </div>
  );
  if (!partnerQ.data) return <LoadingState />;
  const p = partnerQ.data;
  const s = statsQ.data;
  const isSuspended = p.health !== "good";   // health: active→good · suspended→watch · blacklisted→blocked

  return (
    <PageShell
      title={`🌟 客户详情 · ${p.name}`}
      subtitle={
        s
          ? `${s.tier_name} · 月均 ${s.total_orders} 单 · 信用额度 ${formatCents(p.credit_used_cents)} / ${formatCents(p.credit_limit_cents)} · 健康度 ${s.health_score}`
          : "加载中…"
      }
      actions={
        <div className="flex gap-2">
          {canStatus && (
            isSuspended ? (
              <button type="button" disabled={statusMut.isPending} onClick={() => statusMut.mutate("active")} className="h-9 rounded-lg border border-emerald-300 bg-white px-3 text-[13px] font-medium text-emerald-600 hover:bg-emerald-50 disabled:opacity-50">恢复合作</button>
            ) : (
              <button type="button" disabled={statusMut.isPending} onClick={() => { if (confirm(`暂停「${p.name}」？暂停后该客户不能下新单（风控）· 可随时恢复。`)) statusMut.mutate("suspended"); }} className="h-9 rounded-lg border border-rose-300 bg-white px-3 text-[13px] font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50">暂停</button>
            )
          )}
          {canStatus && (
            <button type="button" onClick={() => setTierDialog(true)} className="flex h-9 items-center rounded-lg border border-slate-300 bg-white px-3 text-[13px] font-medium text-slate-600 hover:bg-slate-50">升/降级</button>
          )}
          <Can perm="channel_partners.partners.update">
            <Link to={`/admin/channel-partners/${p.id}/edit`} className="flex h-9 items-center rounded-lg bg-blue-600 px-4 text-[13px] font-semibold text-white hover:bg-blue-700">编辑</Link>
          </Can>
        </div>
      }
    >
      {/* 头部状态条 */}
      {s && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3">
          <span className={`rounded-full px-2.5 py-1 text-[12px] font-semibold ${tierChip(s.tier)}`}>{s.tier_name}</span>
          {s.tier_override && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500" title="手动锁定·不自动重算">🔒 手动</span>}
          <span className={`rounded-full px-2.5 py-1 text-[12px] font-semibold ${ACTIVITY_CHIP[s.activity]}`}>{ACTIVITY_LABEL[s.activity]}</span>
          <span className="text-[13px] text-slate-600">健康度 <b className="text-slate-900">{s.health_score}</b>/100</span>
          <span className="text-slate-300">·</span>
          <span className="text-[13px] text-slate-600">信用额度 <b className="text-slate-900">{formatCents(p.credit_used_cents)}</b> / {formatCents(p.credit_limit_cents)}</span>
          {p.credit_blocked && <span data-testid="header-blocked" className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700">⛔ 已停新单</span>}
          {!s.credit_in_range && s.credit_range_hint && (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700" title={s.credit_range_hint}>⚠️ 额度超{s.tier_name}区间</span>
          )}
        </div>
      )}

      {/* 升/降级 override 弹窗 */}
      {tierDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setTierDialog(false)}>
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-1 text-[15px] font-bold text-slate-900">手动调整分级</div>
            <div className="mb-3 text-[12px] text-slate-500">手动锁定后不再按业务量自动定级 · 进操作日志（D3B）。当前：{s?.tier_name}{s?.tier_override ? "（已锁定）" : "（自动）"}</div>
            <div className="flex flex-col gap-2">
              {(tiersQ.data ?? []).filter((t) => t.is_active).map((t) => (
                <button key={t.code} type="button" disabled={setTierMut.isPending} onClick={() => setTierMut.mutate(t.code)}
                  className={`flex items-center justify-between rounded-lg border px-3 py-2.5 text-left hover:border-blue-400 ${s?.tier === t.code ? "border-blue-500 bg-blue-50" : "border-slate-200"}`}>
                  <div>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tierChip(t.code)}`}>{t.name}</span>
                    <span className="ml-2 text-[11px] text-slate-500">信用 {formatCents(t.credit_min_cents)}–{formatCents(t.credit_max_cents)} · markup {t.baseline_markup_pct}%</span>
                  </div>
                  {s?.tier === t.code && <span className="text-[11px] text-blue-600">当前</span>}
                </button>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-between">
              <button type="button" disabled={setTierMut.isPending} onClick={() => setTierMut.mutate(null)} className="text-[12px] text-slate-500 hover:underline">恢复自动定级</button>
              <button type="button" onClick={() => setTierDialog(false)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-[12px] font-medium text-slate-600">关闭</button>
            </div>
          </div>
        </div>
      )}

      {/* PPR-3 F · 同行站动态：这个同行的订单 / 未处理事件 / 最近登录 */}
      <PartnerSiteActivity partnerId={p.id} />

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)}
            className={`px-4 py-2 text-[13px] ${tab === t ? "border-b-2 border-blue-600 font-semibold text-slate-900" : "text-slate-500 hover:text-slate-900"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "基础信息" && <BasicInfoTab p={p} s={s} />}
      {tab === "专属定价" && <PricingTab partnerId={p.id} />}
      {tab === "信用 & 结算" && <CreditsTab partnerId={p.id} p={p} />}
      {tab === "订单 & 对账" && <SettlementsTab partnerId={p.id} name={p.name} s={s} />}
      {tab === "开放 API" && <PartnerOpenApiTab partnerId={p.id} />}
    </PageShell>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] text-slate-400">{label}</div>
      <div className="text-[13px] text-slate-800">{value || "—"}</div>
    </div>
  );
}

function BasicInfoTab({ p, s }: { p: Partner; s?: PartnerStats }) {
  const joined = p.contract_start_date ?? p.created_at?.slice(0, 10);
  return (
    <div className="flex flex-col gap-4">
      {/* 公司信息 */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="mb-3 text-[14px] font-semibold text-slate-900">🏢 公司信息</div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-3">
          <Field label="公司名" value={p.name} />
          <Field label="税号" value={p.tax_id ?? ""} />
          <Field label="注册地" value={p.billing_address ?? ""} />
          <Field label="入网日期" value={joined ?? ""} />
          <Field label="主联系人" value={p.contact_name} />
          <Field label="电话" value={p.contact_phone ?? ""} />
          <Field label="邮箱" value={p.contact_email} />
          <Field label="结算模式" value={SETTLEMENT_MODE_LABEL[p.settlement_mode]} />
        </div>
      </section>

      {/* PARTNER-ACC-1b · 同行站登录账号（账号管理权限点 accounts.manage；没有权限整张卡不显示）*/}
      <Can perm="channel_partners.accounts.manage">
        <PartnerAccountsCard partnerId={p.id} />
      </Can>

      {/* 业务数据（近 12 月）*/}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="mb-3 text-[14px] font-semibold text-slate-900">📊 业务数据（近 12 月）</div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat label="累计订单" value={String(s?.total_orders ?? 0)} sub="关联订单数" />
          <Stat label="累计流水" value={formatCents(s?.total_gmv_cents ?? 0)} sub="B2B GMV" />
          <Stat label="平均客单" value={formatCents(s?.avg_order_cents ?? 0)} sub="单均金额" />
          <Stat label="投诉率" value={`${s?.complaint_rate_pct ?? 0}%`} sub="代表值 ⏳" />
        </div>
      </section>

      {/* 健康度评分明细 */}
      {s && (
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="mb-3 flex items-center gap-2 text-[14px] font-semibold text-slate-900">
            🏆 健康度评分明细
            <span className="text-[18px] font-bold text-emerald-600">{s.health_score}</span>
            <span className="text-[12px] text-slate-400">/ 100</span>
          </div>
          <div className="flex flex-col gap-2">
            <ScoreRow label="活跃度（真派生）" score={s.score_activity} max={30} />
            <ScoreRow label="付款及时性（代表值 ⏳）" score={s.score_payment} max={30} />
            <ScoreRow label="投诉率（代表值 ⏳）" score={s.score_complaint} max={20} />
            <ScoreRow label="客户经理评价（代表值 ⏳）" score={s.score_am} max={20} />
          </div>
          <div className="mt-3 text-[10px] text-slate-400">⚠️ 客户健康度评分引擎待建 · 仅活跃度为真派生，其余为代表值</div>
        </section>
      )}

      {/* API 对接状态（mirror · 改在 API 集成中心）*/}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-[14px] font-semibold text-slate-900">🔌 API 对接状态</div>
          <Link to="/admin/api-integration" className="flex items-center gap-1 text-[12px] font-medium text-blue-600 hover:underline">
            跳 API 集成中心配置 <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        {p.api_enabled ? (
          <div className="grid grid-cols-1 gap-2.5 md:grid-cols-3">
            <ApiCard icon="📥" title="订单推送" desc="客户下单 → webhook 推到我们" />
            <ApiCard icon="📤" title="凭证推送" desc="我们生成 voucher → 推到客户" />
            <ApiCard icon="📊" title="库存拉取" desc="客户定时拉我们库存" />
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-4 text-center text-[12px] text-slate-500">
            本客户未开启 API 对接 · 可在 API 集成中心配置
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="mt-1 text-[18px] font-bold text-slate-900">{value}</div>
      <div className="text-[10px] text-slate-400">{sub}</div>
    </div>
  );
}

function ScoreRow({ label, score, max }: { label: string; score: number; max: number }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[12px]">
        <span className="text-slate-600">{label}</span>
        <span className="font-semibold text-slate-800">{score}/{max}</span>
      </div>
      <div className="mt-0.5 h-2 rounded-full bg-slate-100">
        <div className="h-2 rounded-full bg-emerald-500" style={{ width: `${(score / max) * 100}%` }} />
      </div>
    </div>
  );
}

function ApiCard({ icon, title, desc }: { icon: string; title: string; desc: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="text-[13px] font-semibold text-slate-800">{icon} {title}</div>
      <div className="mt-1 text-[11px] text-slate-500">{desc}</div>
    </div>
  );
}

function PricingTab({ partnerId }: { partnerId: string }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6">
      <div className="text-[14px] font-semibold text-slate-900">💰 专属定价（mirror · 改在定价管理）</div>
      <div className="mt-2 text-[12px] text-slate-500">本客户的整体系数 / 单个产品的系数或固定金额集中在「售价管理中心 · 客户专属价」配置，越具体越优先、只取一层。</div>
      <Link to={`/admin/pricing/customer-prices?partner_id=${partnerId}`} className="mt-4 inline-flex items-center gap-1 rounded-lg bg-blue-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-blue-700">
        去定价管理配置专属价 <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </section>
  );
}

/** PRICE-CUST-1 · 样稿⑤：同行详情「信用 & 结算」下方的入口——已设几条 + 跳到客户专属价页并选好这个客户（看成本的角色才有）。 */
function CustomerPriceEntry({ partnerId }: { partnerId: string }) {
  const canSee = useCan("pricing.cost.view");
  const q = useQuery({ queryKey: ["pricing", "customer-prices", "partners"], queryFn: () => listCustomerPartners(), enabled: canSee });
  if (!canSee) return null;
  const n = q.data?.find((x) => x.id === partnerId)?.override_count ?? 0;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-[13px]" data-testid="customer-price-entry">
      <span className="text-slate-600">客户专属价：{n > 0 ? <b className="text-slate-900">已设 {n} 条</b> : "未设（价格跟随客群 / 全局）"}</span>
      <Link to={`/admin/pricing/customer-prices?partner_id=${partnerId}`} className="font-medium text-blue-600 hover:underline">查看 / 设置该客户专属价 ›</Link>
    </div>
  );
}

function CreditsTab({ partnerId, p }: { partnerId: string; p: Partner }) {
  const qc = useQueryClient();
  const canApprove = useCan("channel_partners.quotas.approve");
  const canUpdate = useCan("channel_partners.partners.update");
  const credits = useQuery({ queryKey: ["b2b", "credits", partnerId], queryFn: () => partnersApi.listCredits(partnerId) });
  const [amount, setAmount] = useState(500);
  const [reason, setReason] = useState("旺季增加额度");
  const [creditType, setCreditType] = useState<"permanent" | "temporary">("permanent");
  const [newLimit, setNewLimit] = useState(p.credit_limit_cents / 100);

  const submit = useMutation({
    // 用 duration_days 区分类型(固定3650/临时90)·客户经理申请只增额(减额走老板直调)
    mutationFn: () => partnersApi.submitCredit(partnerId, {
      additional_credit_cents: amount * 100, reason,
      duration_days: creditType === "permanent" ? 3650 : 90,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["b2b", "credits", partnerId] }),
    onError: (e) => alert((e as Error).message || "提交额度申请失败"),
  });
  const { ask: askReason, dialog: reasonDialog } = useReasonDialog();   // REASON-1
  const adjustLimit = useMutation({
    mutationFn: (r: ReasonPayload) => partnersApi.update(partnerId, { credit_limit_cents: Math.round(newLimit * 100) || 0, ...r }),   // REASON-1 · 调额度必带原因
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["b2b", "partner", partnerId] });
      qc.invalidateQueries({ queryKey: ["b2b", "partner-stats", partnerId] });
      qc.invalidateQueries({ queryKey: ["b2b", "board"] });
    },
    onError: (e) => alert((e as Error).message || "改额度失败"),
  });
  const decide = useMutation({
    mutationFn: async ({ rid, approve }: { rid: string; approve: boolean }) => {
      const r = await askReason({
        scene: "credit_decision", group: approve ? "approve" : "reject", required: !approve, danger: !approve,
        title: approve ? "批准临时额度" : "拒绝临时额度", subtitle: p.name,
        confirmText: approve ? "确认批准" : "确认拒绝",
      });
      if (!r) throw new ReasonCancelled();
      return partnersApi.decideCredit(partnerId, rid, approve, r);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["b2b", "credits", partnerId] });
      qc.invalidateQueries({ queryKey: ["b2b", "partner", partnerId] });
    },
    onError: (e) => { if (!isReasonCancelled(e)) alert((e as Error).message || "审批失败"); },
  });

  return (
    <div className="flex flex-col gap-4">
      {reasonDialog}
      {/* CREDIT-1 · 样稿 ①：额度总览（授信 / 已用 / 可用 / 逾期 / 预收余额）+ 停单红条 + 结算条件 */}
      <PartnerCreditOverview partnerId={partnerId} />
      <CustomerPriceEntry partnerId={partnerId} />

      {/* 老板/管理员：直接调授信额度（无需审批）*/}
      {canUpdate && canApprove && (
        <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-6">
          <div className="mb-1 text-[14px] font-semibold text-slate-900">🛠 直接调整授信额度（老板 / 管理员 · 调高 / 调低 · 无需审批）</div>
          <div className="mb-3 text-[11px] text-slate-500">老板可直接设定客户基础授信额度（<b className="text-slate-700">减额也在这里：填更低数字</b>）· 立即生效 · 进操作日志（也可在下方审批客户经理的增额申请）</div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[13px] text-slate-500">$</span>
            <input type="number" value={newLimit} onChange={(e) => setNewLimit(Number(e.target.value))} className="h-9 w-40 rounded-lg border border-slate-200 px-3 text-[13px]" />
            <button type="button" disabled={adjustLimit.isPending || Math.round(newLimit * 100) === p.credit_limit_cents} onClick={async () => {
              const r = await askReason({
                scene: "credit_change", title: "调整授信额度", subtitle: p.name,
                changes: [{ label: "基础授信额度", before: formatCents(p.credit_limit_cents), after: formatCents(Math.round(newLimit * 100) || 0) }],
                confirmText: "确认调整",
              });
              if (r) adjustLimit.mutate(r);
            }} className="h-9 rounded-lg bg-violet-600 px-4 text-[13px] font-semibold text-white disabled:opacity-50">
              {adjustLimit.isPending ? "保存中…" : "保存新额度"}
            </button>
            <span className="text-[11px] text-slate-400">当前 {formatCents(p.credit_limit_cents)}</span>
            {adjustLimit.isSuccess && <span className="text-[11px] font-medium text-emerald-600">✓ 已更新</span>}
          </div>
        </div>
      )}

      {/* 客户经理：申请调整授信额度（走老板审批 · 批准后上调基础授信）*/}
      <Can perm="channel_partners.partners.status_manage">
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="mb-1 text-[14px] font-semibold text-slate-900">💳 申请调整授信额度{canApprove ? "（代客户提 · 走审批）" : "（提交老板审批）"}</div>
        <div className="mb-3 text-[11px] text-slate-500">客户经理提交申请 · 老板批准后生效（与临时额度同一审批流 · D3B）</div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={creditType} onChange={(e) => setCreditType(e.target.value as "permanent" | "temporary")} className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-[13px]">
            <option value="permanent">固定额度（永久）</option>
            <option value="temporary">临时额度（90 天）</option>
          </select>
          <input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} className="h-9 w-28 rounded-lg border border-slate-200 px-3 text-[13px]" placeholder="$ 增额" />
          <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} className="h-9 min-w-[180px] flex-1 rounded-lg border border-slate-200 px-3 text-[13px]" placeholder="原因" />
          <button type="button" disabled={submit.isPending} onClick={() => submit.mutate()} className="flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 px-4 text-[13px] font-semibold text-white disabled:opacity-50">
            <ArrowUp className="h-3.5 w-3.5" /> 提交审批
          </button>
        </div>
        <div className="mt-2 text-[11px] text-slate-500">
          {creditType === "permanent"
            ? "固定额度：老板批准 → 永久上调基础授信额度"
            : "临时额度：老板批准 → 90 天临时增额（到期回落 · v2 自动过期）"}
          <span className="ml-1 text-slate-400">· 减额请老板用上方「直接调整」填更低数字</span>
        </div>
      </div>
      </Can>

      <div className="px-1 text-[13px] font-semibold text-slate-700">📋 授信调整申请 {canApprove ? "/ 审批队列" : "· 我的申请"}</div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            <tr><th className="px-4 py-3 text-left">类型</th><th className="px-4 py-3 text-left">金额</th><th className="px-4 py-3 text-left">原因</th><th className="px-4 py-3 text-left">状态</th><th className="px-4 py-3 text-right">操作</th></tr>
          </thead>
          <tbody>
            {(credits.data ?? []).map((c) => (
              <tr key={c.id} className="border-t border-slate-100">
                <td className="px-4 py-3">
                  {c.duration_days >= 365
                    ? <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700">固定</span>
                    : <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">临时 {c.duration_days}天</span>}
                </td>
                <td className="px-4 py-3 font-semibold">
                  <span className={c.additional_credit_cents < 0 ? "text-rose-600" : "text-emerald-600"}>
                    {c.additional_credit_cents < 0 ? "−" : "+"}{formatCents(Math.abs(c.additional_credit_cents))}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-600">{c.reason}</td>
                <td className="px-4 py-3"><Pill tone={c.status === "approved" ? "success" : c.status === "rejected" ? "danger" : "warning"}>{c.status === "pending" ? "待老板审" : c.status === "approved" ? "已批准" : "已拒绝"}</Pill></td>
                <td className="px-4 py-3 text-right">
                  {c.status === "pending" && canApprove ? (
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => decide.mutate({ rid: c.id, approve: true })} className="rounded-md bg-emerald-600 px-2 py-1 text-[11px] font-semibold text-white">批准</button>
                      <button type="button" onClick={() => decide.mutate({ rid: c.id, approve: false })} className="rounded-md bg-rose-600 px-2 py-1 text-[11px] font-semibold text-white">拒绝</button>
                    </div>
                  ) : <span className="text-slate-400">—</span>}
                </td>
              </tr>
            ))}
            {(credits.data ?? []).length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">暂无额度申请</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SettlementsTab({ partnerId, name, s }: { partnerId: string; name: string; s?: PartnerStats }) {
  const qc = useQueryClient();
  const canViewSettle = useCan("channel_partners.settlements.view");   // RBACFIX-1 · 只读结算：view 看对账单；生成对账单另需 process（按钮已用 <Can> 包住）
  const canRegister = useCan("finance.receivables.manage");
  const [payOpen, setPayOpen] = useState(false);
  const allOrders = s?.recent_orders ?? [];

  // 可选月份（从订单 created_at 派生 · 含本月）+ 默认本月
  const thisMonth = new Date().toISOString().slice(0, 7);
  const months = useMemo(() => {
    const set = new Set<string>([thisMonth, ...allOrders.map((o) => o.created_at.slice(0, 7))]);
    return Array.from(set).sort().reverse();
  }, [allOrders, thisMonth]);
  const [month, setMonth] = useState(thisMonth);

  const monthOrders = allOrders.filter((o) => o.created_at.slice(0, 7) === month);
  const monthTotal = monthOrders.reduce((a, o) => a + o.amount_cents, 0);

  return (
    <div className="flex flex-col gap-4">
      {/* CREDIT-1 · 样稿 ②④：应收明细（一单一笔）+ 月度对账单 */}
      {canViewSettle ? (
        <>
          <ReceivablesCard partnerId={partnerId} onRegister={canRegister ? () => setPayOpen(true) : undefined} />
          <PartnerPaymentsCard partnerId={partnerId} canVoid={canRegister} />   {/* REASON-2 · 收款记录 + 作废 */}
          <PartnerStatementsCard partnerId={partnerId} />
        </>
      ) : (
        <section className="rounded-xl border border-slate-200 bg-white p-5 text-center text-[12px] text-slate-400">没有查看应收 / 对账单的权限</section>
      )}
      {payOpen && <RegisterPaymentModal partnerId={partnerId} partnerName={name} onClose={() => setPayOpen(false)} />}

      {/* 订单查询（按月份）*/}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="text-[14px] font-semibold text-slate-900">📦 订单查询 · {name}</div>
          <select value={month} onChange={(e) => setMonth(e.target.value)} className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-[12px]">
            {months.map((m) => <option key={m} value={m}>{m.replace("-", " 年 ")} 月</option>)}
          </select>
        </div>
        <div className="mb-2 text-[12px] text-slate-500">{month} · 共 <b className="text-slate-800">{monthOrders.length}</b> 单 · 合计 <b className="text-slate-800">{formatCents(monthTotal)}</b></div>
        {monthOrders.length === 0 ? (
          <div className="py-6 text-center text-[12px] text-slate-400">该月无订单</div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <tr><th className="px-3 py-2 text-left">订单号</th><th className="px-3 py-2 text-right">人数</th><th className="px-3 py-2 text-right">金额</th><th className="px-3 py-2 text-left">状态</th></tr>
              </thead>
              <tbody>
                {monthOrders.map((o) => (
                  <tr key={o.order_no} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-medium text-slate-900">{o.order_no}</td>
                    <td className="px-3 py-2 text-right">{o.pax}</td>
                    <td className="px-3 py-2 text-right font-semibold">{formatCents(o.amount_cents)}</td>
                    <td className="px-3 py-2"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{o.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
